import {
	Affix,
	Alert,
	Button,
	Card,
	Col,
	Layout,
	Progress,
	Row,
	Space,
	Tooltip,
	Typography,
} from "antd";
import { primaryDark } from "../GlobalConfig";
import { useSelector } from "react-redux";
import { Content } from "antd/lib/layout/layout";
import {
	EnvironmentFilled,
	FacebookOutlined,
	InfoCircleOutlined,
	InstagramOutlined,
	LinkedinOutlined,
	MailFilled,
	PhoneFilled,
	YoutubeOutlined,
} from "@ant-design/icons";
import { isMobile } from "react-device-detect";

const totalSteps = 5;
const paddingBottom = 48;
const maxWidth = 1300;
const padding = isMobile ? 12 : 16;
const headerStyle = {
	borderBottom: "1px solid #eee",
	padding: isMobile ? '8px 12px' : padding,
	textAlign: "center",
	background: "#ffffff",
};

function ContentContainer(props) {
	const {
		children,
		pageTitle,
		extra,
		showNext = true,
		step,
		stepPercentage,

		backButton,
		themeColor,
	} = props;

	const standaloneMode = useSelector(
		(state) => state.locations.currentLocation?.standaloneMode,
	);

	const logoURL = useSelector(
		(state) => state.locations.currentLocation?.logo?.url,
	);
	const coverPhoto = useSelector(
		(state) => state.locations.currentLocation?.coverPhoto,
	);
	const showPhone = useSelector(
		(state) => state.locations.currentLocation?.showPhoneNumber,
	);
	const showEmailAddress = useSelector(
		(state) => state.locations.currentLocation?.showEmailAddress,
	);
	const showSocialLinks = useSelector(
		(state) => state.locations.currentLocation?.showSocialLinks,
	);

	const facebookUrl = useSelector((state) => state.locations.currentLocation?.facebookUrl);
	const instagramUrl = useSelector(
		(state) => state.locations.currentLocation?.instagramUrl,
	);
	const linkedinUrl = useSelector(
		(state) => state.locations.currentLocation?.linkedinUrl,
	);
	const youtubeUrl = useSelector(
		(state) => state.locations.currentLocation?.youtubeUrl,
	);
	const phone = useSelector((state) => state.locations.currentLocation?.phone);
	const email = useSelector((state) => state.locations.currentLocation?.email);
	const fullAddress = useSelector(
		(state) => state.locations.currentLocation?.location?.fullAddress,
	);
	const name = useSelector((state) => state.locations.currentLocation?.name);
	const bookingMessageContent = useSelector(
		(state) => state.locations.currentLocation?.bookingMessageContent,
	);
	const bookingMessageTitle = useSelector(
		(state) => state.locations.currentLocation?.bookingMessageTitle,
	);
	const allowDismissableBookingMessage = useSelector(
		(state) => state.locations.currentLocation?.allowDismissableBookingMessage,
	);

	// Note: usePageView is called by individual page components with step info
	// Don't call it here to avoid duplicate tracking

	const formatUrl = (url = '') => {
		if (!url) return '';
		return url.startsWith('http://') || url.startsWith('https://')
			? url
			: `https://${url}`;
	};


	const footer = (
		<Layout.Footer style={{ textAlign: "center", borderTop: "1px solid #eee", padding: isMobile ? '8px 12px' : undefined }}>
			<Space size={isMobile ? "middle" : "large"} wrap>
				<Typography.Link
					type="secondary"
					href="https://www.zavy360.com/terms"
					style={isMobile ? { fontSize: 12 } : undefined}
				>
					Terms of Use
				</Typography.Link>
				<Typography.Link
					type="secondary"
					href="https://www.zavy360.com"
					style={isMobile ? { fontSize: 12 } : undefined}
				>
					Powered by Zavy360
				</Typography.Link>
				<Typography.Link
					type="secondary"
					href="https://www.zavy360.com/zavy-data-policy"
					style={isMobile ? { fontSize: 12 } : undefined}
				>
					Privacy Policy
				</Typography.Link>
			</Space>
		</Layout.Footer>
	);

	const pageHeader = (
		<>
			<Row
				justify="space-between"
				style={headerStyle}
				className="zavy-header"
				align="middle"
			>
				<Col> {backButton}</Col>

				{standaloneMode && (
					<Col>
						<img
							style={{ maxHeight: isMobile ? 48 : 80, maxWidth: isMobile ? 160 : 200 }}
							src={logoURL}
							alt="Practice Logo"
						/>
					</Col>
				)}

				<Col>
					{showNext ? (
						<Button disabled type="primary">
							Next
						</Button>
					) : (
						extra || null
					)}
				</Col>
			</Row>
		</>
	);

	const pageTitleElement = (
		<div style={{ padding: isMobile ? '16px 0 8px' : '16px 0' }}>
			<Typography.Title
				level={isMobile ? 4 : 3}
				style={{ marginTop: 0, marginBottom: 8 }}
			>
				{pageTitle}
			</Typography.Title>
			<Tooltip
				title={
					<Typography.Text
						style={{ marginRight: 16, color: "#fff" }}
						type="secondary"
					>
						Step {step} of {totalSteps}
					</Typography.Text>
				}
			>
				<Progress
					style={{ width: isMobile ? 80 : 100 }}
					percent={stepPercentage}
					showInfo={false}
					strokeColor={themeColor ? themeColor : primaryDark}
					size="small"
				/>
			</Tooltip>
		</div>
	);

	const sidebar = (
		<>
			{bookingMessageContent && (
				<Alert
					icon={<InfoCircleOutlined />}
					style={{
						background: "#fff",
						border: "1px solid #d9d9d9",
					}}
					showIcon
					message={
						<Typography.Text>{bookingMessageTitle}</Typography.Text>
					}
					description={bookingMessageContent}
					closable={allowDismissableBookingMessage}
				/>
			)}
			<div style={{ marginTop: bookingMessageContent ? 24 : 0 }} />
			<Card
				bordered
				style={{ width: "100%" }}
				bodyStyle={{ background: "#fff" }}
				cover={coverPhoto &&
					<img
						src={coverPhoto}
						style={{ maxHeight: 300, width: "100%", objectFit: "cover", position: "relative" }}
						alt="Practice Logo"
					/>
				}
			>
				<Space direction="vertical" size="small" style={{ width: "100%" }}>
					<Typography.Title level={5}>{name}</Typography.Title>
					{showPhone &&
						<Typography.Link style={{ color: 'black' }} type="secondary" href={`tel:${phone}`}>
							<Space>
								<Button type='default' shape='circle' icon={<PhoneFilled />} />
								{phone}
							</Space>
						</Typography.Link>
					}

					{showEmailAddress &&
						<Typography.Link style={{ color: 'black' }} type="secondary" href={`mailto:${email}`}>
							<Space>
								<Button type='default' shape='circle' icon={<MailFilled />} />
								{email}
							</Space>
						</Typography.Link>
					}


					<Typography.Link style={{ color: 'black' }} type="secondary" href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(fullAddress)}`} target="_blank" rel="noopener noreferrer">
						<Space>
							<Button type='default' shape='circle' icon={<EnvironmentFilled />} />
							{fullAddress}
						</Space>
					</Typography.Link>
				</Space>
				{/* Add Google Map iframe */}
				<div style={{ marginTop: 8 }}>
					<iframe
						title="Google Map"
						width="100%"
						height="200"
						style={{ border: 0, borderRadius: 4, marginTop: 36 }}
						loading="lazy"
						allowFullScreen
						src={`https://www.google.com/maps?q=${encodeURIComponent(fullAddress)}&output=embed`}
					/>
				</div>

				<Space style={{ marginTop: 8, display: "flex", justifyContent: 'center' }}>
					{showSocialLinks && (
						<>
							{facebookUrl && (
								<a href={formatUrl(facebookUrl)} target="_blank" rel="noopener noreferrer">
									<Button shape="circle" icon={<FacebookOutlined />} />
								</a>
							)}
							{instagramUrl && (
								<a href={formatUrl(instagramUrl)} target="_blank" rel="noopener noreferrer">
									<Button shape="circle" icon={<InstagramOutlined />} />
								</a>
							)}
							{youtubeUrl && (
								<a href={formatUrl(youtubeUrl)} target="_blank" rel="noopener noreferrer">
									<Button shape="circle" icon={<YoutubeOutlined />} />
								</a>
							)}
							{linkedinUrl && (
								<a href={formatUrl(linkedinUrl)} target="_blank" rel="noopener noreferrer">
									<Button shape="circle" icon={<LinkedinOutlined />} />
								</a>
							)}
						</>
					)}
				</Space>
			</Card>
		</>
	);

	if (standaloneMode) {
		return (
			<>
				<Layout
					style={{
						background: isMobile ? "#fff" : "#fafafa",
						display: "flex",
						flexGrow: 1,
						flex: 1,
						overflow: "hidden",
						height: "100vh",
					}}
				>
					{/* Header - sticky at top, constrained to match content width */}
					<div style={{
						position: "sticky",
						top: 0,
						zIndex: 1000,
						background: "#ffffff",
						borderBottom: "1px solid #eee",
					}}>
						<div style={{
							maxWidth: maxWidth,
							margin: "0 auto",
							padding: isMobile ? '8px 12px' : '16px',
						}}>
							<Row
								justify="space-between"
								align="middle"
							>
								<Col>{backButton}</Col>
								<Col>
									<img
										style={{ maxHeight: isMobile ? 48 : 80, maxWidth: isMobile ? 160 : 200 }}
										src={logoURL}
										alt="Practice Logo"
									/>
								</Col>
								<Col>
									{showNext ? (
										<Button disabled type="primary">
											Next
										</Button>
									) : (
										extra || null
									)}
								</Col>
							</Row>
						</div>
					</div>
					<Content style={{ flex: 1, overflowY: "auto" }}>
						{isMobile ? (
							/* Mobile: single column, no card wrapper, tight padding */
							<div style={{ padding: '0 12px 24px' }}>
								{pageTitleElement}
								{children}
							</div>
						) : (
							/* Desktop: 2-column layout with sidebar */
							<div style={{ maxWidth: maxWidth, margin: "0 auto", padding: "0 16px" }}>
								<div style={{ padding: "18px 0 8px 0" }}>{pageTitleElement}</div>
								<Row
									justify="space-between"
									gutter={[24, 24]}
								>
									<Col md={24} lg={16}>
										<Card>{children}</Card>
										<div style={{ marginTop: 36 }} />
									</Col>
									<Col md={24} lg={8}>
										{sidebar}
									</Col>
								</Row>
							</div>
						)}
					</Content>
					{footer}
				</Layout>
			</>
		);
	}

	return (
		<>
			<div
				style={{
					maxWidth: 800,
					width: "100%",
					marginLeft: "auto",
					marginRight: "auto",
					paddingBottom: paddingBottom,
				}}
			>
				<Affix>{pageHeader}</Affix>
				<div style={{ padding: padding }}>
					{pageTitleElement}
					{children}
				</div>
				{footer}
			</div>
		</>
	);
}

export default ContentContainer;
